import React, { useMemo } from "react";
import { ReportType } from "@/api/fetchQuickBooksReports/types";
import { useQuickBooksReports } from "../../hooks/useQuickBooksReports";
import { createQuickBooksReportsColumns } from "../QuickBooksReportsColumns/QuickBooksReportsColumns";
import MainPageWrapper from "../../../../../../components/MainPageWrapper/MainPageWrapper";
import DataTable from "@/components/Datatable/Datatable";
import { Button } from "@/components/ui/button";
import { useQuickBooksLastSync } from "../../hooks/useQuickBooksLastSync";
import { formatSnapshotDateTime } from "../../utils/formatSnapshotDateTime";

const MonthlyBalanceSheet: React.FC = () => {
  const {
    reports,
    totalCount,
    loading,
    error,
    sorting,
    pagination,
    handleSortingChange,
    handlePaginationChange,
    handleDownloadReport,
  } = useQuickBooksReports(ReportType.MONTHLY_BALANCE_SHEET);
  const {
    loading: lastSyncLoading,
    lastSuccessfulSyncAt,
    latestReportUuid,
    latestReportSnapshotAt,
    latestReportDate,
  } = useQuickBooksLastSync();

  const syncLine = lastSuccessfulSyncAt
    ? `Last successful sync: ${formatSnapshotDateTime(lastSuccessfulSyncAt)}`
    : latestReportUuid
      ? "No recent sync"
      : "No sync yet";

  const latestFileTime = latestReportSnapshotAt
    ? formatSnapshotDateTime(latestReportSnapshotAt)
    : latestReportDate
      ? new Date(latestReportDate).toLocaleDateString(undefined, {
          dateStyle: "medium",
          timeZone: "UTC",
        })
      : null;

  const columns = useMemo(
    () => createQuickBooksReportsColumns({ handleDownloadReport }),
    [handleDownloadReport],
  );

  return (
    <MainPageWrapper error={error} title="Monthly Balance Sheet Report">
      <div className="mb-4 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-normal text-gray-900 dark:text-white">
              Latest Balance Sheet
            </h2>
            {!lastSyncLoading && (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {syncLine}
                </p>
                {latestReportUuid && latestFileTime && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {`Latest file: ${latestFileTime}`}
                  </p>
                )}
              </>
            )}
          </div>
          {latestReportUuid && (
            <Button
              aria-label="Download latest"
              className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700"
              onClick={() => handleDownloadReport(latestReportUuid)}
              variant="ghost"
            >
              Download
            </Button>
          )}
        </div>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Each file is a snapshot of your QuickBooks data at the time shown. The
          newest file restates earlier months with any later changes.
        </p>
      </div>
      <DataTable
        columns={columns}
        data={reports}
        error={error}
        loading={loading}
        noDataMessage="No reports found"
        onPageChange={(newPageIndex, newPageSize) =>
          handlePaginationChange({
            pageIndex: newPageIndex,
            pageSize: newPageSize,
          })
        }
        onSortingChange={handleSortingChange}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        rowKeyExtractor={(item) => item.uuid}
        sorting={sorting}
        totalCount={totalCount}
      />
    </MainPageWrapper>
  );
};

export default MonthlyBalanceSheet;
